/**
 * Camada 1c · A ARMADILHA DE FOCO DA GAVETA DO CELULAR.
 *
 * =====================================================================================
 * O DEFEITO QUE ELA FECHA, medido em 06/09/2026 na suite do VOE, em 390x844 com toque
 * =====================================================================================
 *
 * Com a gaveta do celular aberta, a 15a parada do Tab e o "Sair", e a 16a era o botao
 * "Abrir menu" da topbar. A topbar naquele momento esta ATRAS da cortina: o rail sobe para
 * `z-painel` no celular e a cortina cobre a tela inteira (`Rail.tsx:165-199`).
 *
 * ENTAO O FOCO CAIA NUM CONTROLE QUE QUEM USA O DEDO NAO ALCANCA. Nao e detalhe de
 * conformidade no papel: quem navega por teclado ou por leitor de tela ficava com o foco num
 * lugar que a tela nao mostra, sem nenhum sinal de que tinha saido do menu, e sem o caminho
 * de volta que o toque tem (o dedo fecha a gaveta tocando a cortina; o teclado nao chegava
 * nela).
 *
 * =====================================================================================
 * POR QUE ELA MORA AQUI, E NAO EM `layout`, QUE E ONDE A GAVETA E DESENHADA
 * =====================================================================================
 *
 * `AppShell.tsx` e `Rail.tsx` estao CONGELADOS por ordem dele em 06/09/2026, com duas
 * capturas do celular: "ESSE APP VOE E MOBILE FIRST - TEM QUE FICAR EXATAMENTE ASSIM". Um
 * hook recusa a escrita nos dois.
 *
 * A ARMADILHA NAO PRECISA DAQUELE ESTADO. Prender o foco e comportamento de documento, e nao
 * de desenho: resolve-se inteira por leitura do DOM e por uma tecla, e o `Chassi` e quem
 * monta o `AppShell` sem estar congelado. Nenhum pixel daquelas capturas muda, e nenhuma
 * linha dos moldes e tocada.
 *
 * =====================================================================================
 * COMO ELA SABE QUE A GAVETA ESTA ABERTA, sem enxergar o estado do React
 * =====================================================================================
 *
 * PELA CORTINA, e ela e o sinal exato de que ha um modal na tela. So e desenhada quando
 * `abertoNoCelular` e verdadeiro, e leva `md:hidden`, entao do tablet para cima nasce com
 * `display: none` mesmo com a gaveta aberta no estado. Medir a cortina VISIVEL responde as
 * duas perguntas de uma vez: a gaveta esta aberta E esta em modo modal nesta largura. Ler o
 * estado do React responderia so a primeira, e a armadilha passaria a prender o foco no
 * computador, onde o rail e coluna comum e o Tab TEM que sair dele.
 *
 * NAO HA ESTADO GUARDADO AQUI, e isso e deliberado. A armadilha e reavaliada a cada tecla, a
 * partir do DOM. Uma armadilha com estado proprio pode continuar ligada depois de a gaveta
 * fechar, e ai ela prende para sempre quem usa leitor de tela, que e MUITO pior que o defeito
 * que ela veio corrigir. Sem estado, nao ha o que ficar preso.
 */

/**
 * Os candidatos a foco dentro da gaveta.
 *
 * `[tabindex="-1"]` fica de fora porque ele e justamente a marca de "focavel por codigo, e
 * nao pela tabulacao", e inclui-lo mudaria o percurso que a pessoa faz de verdade.
 */
const SELETOR_FOCAVEL = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Visivel medido pela GEOMETRIA, e nao pela classe.
 *
 * E a mesma decisao que a suite de acessibilidade ja tomou: classe existir no arquivo nao
 * prova que alguem desenhou. `display: none` devolve caixa zerada, e e assim que a cortina no
 * computador e o botao de recolher (que e `hidden md:flex`) saem da conta sozinhos.
 */
function estaVisivel(elemento: HTMLElement): boolean {
  const caixa = elemento.getBoundingClientRect();
  return caixa.width > 0 && caixa.height > 0;
}

/**
 * Acha o `<nav>` da gaveta pelo cartao da conta.
 *
 * POR QUE POR ELE, e nao pelo `aria-label` do `<nav>`: aquele rotulo passa pelo dicionario
 * (`t('Menu do produto')`), entao MUDA com o idioma, e uma armadilha que so funciona em
 * portugues e uma armadilha que nao funciona. `data-conta="true"` e atributo de dado, posto
 * em `Rail.tsx:452` exatamente para nao depender de texto nem de classe de estilo.
 *
 * E por que nao `document.querySelector('nav')` direto: no celular existem DOIS `<nav>` na
 * tela, o rail e a barra inferior, e o primeiro do DOM nem sempre e o que se quer.
 */
function acharGaveta(): HTMLElement | null {
  const conta = document.querySelector('[data-conta="true"]');
  if (!conta) return null;
  return conta.closest('nav');
}

/**
 * A cortina, que e a irma ANTERIOR do `<nav>` no DOM (`Rail.tsx:165`).
 *
 * Devolve `null` quando ela nao existe (gaveta fechada) ou nao desenha (tablet para cima), e
 * nos dois casos isso significa o mesmo para quem chama: nao ha modal, nao ha armadilha.
 */
function acharCortina(gaveta: HTMLElement): HTMLElement | null {
  const anterior = gaveta.previousElementSibling;
  // `tagName` e nao `instanceof`: o segundo falha quando o no vem de outro contexto de
  // janela, e o custo de errar aqui seria a armadilha nao ligar, em silencio.
  if (!anterior || anterior.tagName !== 'BUTTON') return null;
  const cortina = anterior as HTMLElement;
  return estaVisivel(cortina) ? cortina : null;
}

/**
 * O botao das tres barras, que e para onde o foco volta quando a gaveta fecha.
 *
 * O CAMINHO E ESTRUTURAL, e nao pelo rotulo: ele e o unico `<button>` filho DIRETO do
 * `<header>` (`Topbar.tsx:57`), porque os outros cinco moram dentro do bloco da direita.
 * Procurar por `aria-label="Abrir menu"` obrigaria este arquivo a repetir um texto que mora
 * no dicionario, e a devolucao do foco quebraria em ingles sem ninguem perceber.
 */
function acharBotaoQueAbre(): HTMLElement | null {
  const cabecalho = document.querySelector('header');
  const botao = cabecalho?.querySelector<HTMLElement>(':scope > button') ?? null;
  if (!botao) return null;
  return estaVisivel(botao) ? botao : null;
}

/**
 * LIGA A ARMADILHA, e devolve a funcao que a desliga.
 *
 * Separada do gancho de React de proposito: assim ela pode ser exercitada sem montar
 * componente nenhum.
 */
export function ligarArmadilhaDeFoco(): () => void {
  /**
   * O ULTIMO ELEMENTO FOCADO FORA DE QUALQUER MENU.
   *
   * Ele existe porque, quando a pessoa aperta `Escape`, o foco esta DENTRO da gaveta, e a
   * pergunta "de onde ela veio" ja nao tem resposta no `document.activeElement`. Guardar o de
   * fora e o que permite devolver o foco a origem, e nao a um lugar qualquer.
   *
   * O filtro e `closest('nav')` e nao a gaveta em si: e mais barato, porque sobe poucos
   * niveis e nao varre o documento, e ainda serve, ja que o que interessa guardar e o que
   * esta na topbar e na area util, nunca o que esta dentro de um menu.
   */
  let ultimoFocoDeFora: HTMLElement | null = null;

  const aoFocar = (evento: FocusEvent) => {
    const alvo = evento.target as HTMLElement | null;
    if (!alvo || typeof alvo.closest !== 'function') return;
    if (alvo.closest('nav')) return;
    ultimoFocoDeFora = alvo;
  };

  /**
   * Devolve o foco DEPOIS de a gaveta fechar de verdade.
   *
   * DUAS MOLDURAS, e nao uma: quem fecha a gaveta e o `AppShell`, com estado de React, e
   * mover o foco no mesmo instante do `keydown` acertaria um botao que ainda esta coberto
   * pela cortina. A segunda moldura acontece depois de o React ter pintado.
   *
   * E A CONFERENCIA ANTES DE MOVER E O QUE TORNA ISTO SEGURO: se a gaveta continuar aberta
   * por qualquer motivo, o foco fica onde esta. Melhor nao devolver o foco do que joga-lo
   * para fora de um modal que continua na tela.
   */
  const devolverFocoQuandoFechar = (destino: HTMLElement | null) => {
    if (!destino) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const gaveta = acharGaveta();
        if (gaveta && acharCortina(gaveta)) return;
        if (!destino.isConnected || !estaVisivel(destino)) return;
        destino.focus();
      });
    });
  };

  const aoTeclar = (evento: KeyboardEvent) => {
    /**
     * O FILTRO DE TECLA VEM PRIMEIRO, e e ele que torna aceitavel um ouvinte permanente no
     * documento. `AppShell.tsx:200` registra a mesma preocupacao para o `Escape` dele:
     * ouvinte sempre ligado inspeciona toda digitacao do sistema, inclusive dentro de campo
     * de texto. Aqui a digitacao comum sai na primeira comparacao de string, antes de
     * qualquer leitura do DOM.
     */
    if (evento.key !== 'Tab' && evento.key !== 'Escape') return;

    const gaveta = acharGaveta();
    if (!gaveta) return;
    // Sem cortina visivel nao ha modal: ou a gaveta esta fechada, ou a tela e de tablet para
    // cima, onde o rail e coluna no fluxo e o Tab TEM que sair dele.
    if (!acharCortina(gaveta)) return;

    if (evento.key === 'Escape') {
      /**
       * NAO CHAMAMOS `preventDefault` NEM `stopPropagation`, e este e o ponto mais delicado
       * do arquivo.
       *
       * Quem fecha a gaveta e o `AppShell` (`AppShell.tsx:203-210`), com um ouvinte em
       * `window`. Este aqui roda antes, na fase de captura do documento. Barrar o evento
       * deixaria a gaveta ABERTA com o `Escape` apertado, que e exatamente a pessoa presa que
       * esta armadilha existe para evitar. `Escape` precisa SEMPRE funcionar.
       */
      devolverFocoQuandoFechar(ultimoFocoDeFora ?? acharBotaoQueAbre());
      return;
    }

    const focaveis = Array.from(gaveta.querySelectorAll<HTMLElement>(SELETOR_FOCAVEL)).filter(
      estaVisivel,
    );
    // FALHA SEGURA: sem nada focavel dentro, nao ha ciclo possivel, e prender o foco seria
    // prende-lo no vazio. Deixa passar.
    if (focaveis.length === 0) return;

    const primeiro = focaveis[0]!;
    const ultimo = focaveis[focaveis.length - 1]!;
    const atual = document.activeElement as HTMLElement | null;
    const estaDentro = !!atual && gaveta.contains(atual);

    if (!estaDentro) {
      // O foco esta fora com o modal na tela: pode ser a cortina, o `body`, ou o proprio botao
      // das tres barras logo depois de abrir por clique. O primeiro Tab entra na gaveta, que e
      // o que qualquer camada sobreposta faz.
      evento.preventDefault();
      (evento.shiftKey ? ultimo : primeiro).focus();
      return;
    }

    // O CICLO. Depois do ultimo volta ao primeiro, e antes do primeiro vai ao ultimo. Sem as
    // duas metades a armadilha vazaria para tras, que e o mesmo defeito virado ao contrario, e
    // mais dificil de notar porque quase ninguem tabula para tras conferindo.
    if (!evento.shiftKey && atual === ultimo) {
      evento.preventDefault();
      primeiro.focus();
      return;
    }
    if (evento.shiftKey && atual === primeiro) {
      evento.preventDefault();
      ultimo.focus();
    }
  };

  /**
   * CAPTURA no documento, e nao borbulha.
   *
   * Assim a armadilha e alcancada mesmo que algum componente no caminho pare a propagacao do
   * evento, e o `Escape` continua chegando ao `AppShell` depois, porque nada e barrado aqui.
   * `focusin` no lugar de `focus` porque este ultimo nao borbulha.
   */
  document.addEventListener('keydown', aoTeclar, true);
  document.addEventListener('focusin', aoFocar, true);

  return () => {
    document.removeEventListener('keydown', aoTeclar, true);
    document.removeEventListener('focusin', aoFocar, true);
  };
}
